/**
 * Hook `useAdminApp`
 * 
 * Este hook proporciona funcionalidades para gestionar aplicaciones de administración, incluyendo la carga, creación, actualización, eliminación de apps y manejo del estado asociado.
 * 
 * @returns {object} Un objeto que contiene el estado de las apps y funciones para manejar dichas apps.
 */

import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/redux/index'
import {
  createApp,
  deleteApp,
  getApps,
  updateApp,
} from 'src/services/IAM/AdminApps/AdminApp.service'
import { useAlert } from 'src/hooks/useAlert'
import { IAdminApp } from 'src/services/IAM/AdminApps/AdminApp.interface'
import { setApp, setAppUpdated, resetApps } from 'src/redux/IAM/AdminApps/adminAppsSlice'
import useLoading from 'src/hooks/useLoading'

export const useAdminApp = () => {
  const { showAlert } = useAlert()
  const { startLoading, stopLoading } = useLoading()
  const dispatch = useDispatch<AppDispatch>()
  const { app, apps, loading, error, appUpdated } = useSelector(
    (state: RootState) => state.apps,
  )

  /**
   * Carga la lista de aplicaciones con paginación.
   * Muestra alertas según el resultado de la operación.
   *
   * @function loadApps
   * @param {object} pagination - Parámetros de paginación para cargar las aplicaciones.
   * @param {number} [pagination.currentPage=1] - Página actual para la paginación.
   * @param {number} [pagination.pageSize=25] - Tamaño de la página para la paginación.
   * @throws {Error} Si ocurre un error inesperado durante la carga de las apps.
   */
  const loadApps = ({
    currentPage = 1,
    pageSize = 25,
  }: {
    currentPage?: number
    pageSize?: number
  }) => {
    try {
      startLoading()
      dispatch(
        getApps({
          currentPage,
          pageSize,
        }),
      )
        .then((result: any) => {
          stopLoading()
          if (getApps.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al cargar las apps',
            )
          } else if (getApps.fulfilled.match(result)) {
            showAlert('success', 'Apps cargadas correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
      showAlert('success', 'Apps cargadas correctamente')
    } catch (error) {
      showAlert('error', 'Error al cargar las apps')
    }
  }

  /**
   * Establece una aplicación actualizada en el estado.
   *
   * @function setAppAdminUpdated
   * @param {IAdminApp} app - La aplicación que ha sido actualizada.
   */
  const setAppAdminUpdated = (app: IAdminApp) => {
    dispatch(setAppUpdated(app))
  }

  /**
   * Establece una aplicación en el estado.
   *
   * @function setAppAdmin
   * @param {IAdminApp} app - La aplicación que se va a establecer en el estado.
   */
  const setAppAdmin = (app: IAdminApp) => {
    dispatch(setApp(app))
  }

  /**
   * Elimina una aplicación por su ID.
   * Muestra alertas según el resultado de la operación.
   *
   * @function removeApp
   * @param {number} appId - El ID de la aplicación que se va a eliminar.
   */
  const removeApp = (appId: number) => {
    startLoading()
    dispatch(deleteApp(appId))
      .then((result) => {
        stopLoading()
        if (deleteApp.rejected.match(result)) {
          showAlert(
            'error',
            String(result.payload) || 'Error al eliminar la app',
          )
        } else if (deleteApp.fulfilled.match(result)) {
          showAlert('success', 'App eliminada correctamente')
          loadApps({
            currentPage: 1,
            pageSize: 25,
          })
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Actualiza una aplicación existente.
   * Muestra alertas según el resultado de la operación.
   *
   * @function updateAdminApp
   * @param {IAdminApp} app - La aplicación que se va a actualizar.
   */
  const updateAdminApp = (app: IAdminApp) => {
    startLoading()
    dispatch(updateApp(app))
      .then((result) => {
        stopLoading()
        if (updateApp.rejected.match(result)) {
          showAlert(
            'error',
            String(result.payload) || 'Error al actualizar la app',
          )
        } else if (updateApp.fulfilled.match(result)) {
          showAlert('success', 'App actualizada correctamente')
          loadApps({
            currentPage: 1,
            pageSize: 25,
          })
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Crea una nueva aplicación.
   * Muestra alertas según el resultado de la operación.
   *
   * @function createAdminApp
   * @param {IAdminApp} app - Los datos de la nueva aplicación que se va a crear.
   */
  const createAdminApp = (app: IAdminApp) => {
    startLoading()
    dispatch(createApp(app))
      .then((result: any) => {
        stopLoading()
        if (createApp.rejected.match(result)) {
          showAlert('error', String(result.payload) || 'Error al crear la app')
        } else if (createApp.fulfilled.match(result)) {
          showAlert('success', 'App creada correctamente')
          loadApps({
            currentPage: 1,
            pageSize: 25,
          })
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Reinicia el estado de las aplicaciones, eliminando todos los datos.
   *
   * @function clearApps
   */
  const clearApps = () => {
    dispatch(resetApps())
  }

  return {
    app,
    apps,
    loading,
    error,
    appUpdated,
    updateAdminApp,
    setAppAdminUpdated,
    createAdminApp,
    setAppAdmin,
    loadApps,
    removeApp,
    clearApps,
  }
}

export default useAdminApp
